import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, TrendingUp, TrendingDown, Activity, Calendar,
  AlertTriangle, CheckCircle, Clock, Target, RefreshCw,
  ChevronLeft, ChevronRight, Building2, User, Edit2,
  Eye, Palmtree, XCircle, Minus
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const KPICard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg ${color.replace('text-', 'bg-').replace('600', '100')}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
      {trend && (
        <div className="flex items-center gap-1 mt-2">
          {trend > 0 ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          <span className={`text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '+' : ''}{trend}% vs luna trecută
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

const EmployeeCapacityRow = ({ employee, onEditCapacity }) => {
  const statusColors = {
    underload: 'bg-red-100 text-red-700',
    low_load: 'bg-yellow-100 text-yellow-700',
    optimal: 'bg-green-100 text-green-700',
    high_load: 'bg-orange-100 text-orange-700',
    burnout_risk: 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    underload: 'Underload',
    low_load: 'Low Load',
    optimal: 'Optimal',
    high_load: 'High Load',
    burnout_risk: 'Burnout Risk'
  };

  const utilizationColor = employee.status_color === 'green' ? 'bg-green-500' :
    employee.status_color === 'yellow' ? 'bg-yellow-500' :
    employee.status_color === 'orange' ? 'bg-orange-500' : 'bg-red-500';

  return (
    <tr className="border-b hover:bg-slate-50">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
            <User className="w-4 h-4 text-slate-600" />
          </div>
          <div>
            <p className="font-medium">{employee.user_name}</p>
            <p className="text-xs text-muted-foreground capitalize">
              {employee.role.replace('_', ' ')}
            </p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-center">
        <div className="flex items-center gap-2 justify-center">
          <span>{employee.daily_capacity_hours}h/zi</span>
          <button 
            onClick={() => onEditCapacity(employee)}
            className="p-1 hover:bg-slate-100 rounded"
          >
            <Edit2 className="w-3 h-3 text-muted-foreground" />
          </button>
        </div>
      </td>
      <td className="py-3 px-4 text-center">
        {employee.total_capacity_hours.toFixed(0)}h
      </td>
      <td className="py-3 px-4 text-center">
        {employee.actual_total_hours.toFixed(1)}h
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full ${utilizationColor} transition-all`}
              style={{ width: `${Math.min(employee.utilization_pct * 100, 100)}%` }}
            />
          </div>
          <span className="text-sm font-medium w-12 text-right">
            {(employee.utilization_pct * 100).toFixed(0)}%
          </span>
        </div>
      </td>
      <td className="py-3 px-4 text-center">
        <Badge className={statusColors[employee.status]}>
          {statusLabels[employee.status]}
        </Badge>
      </td>
    </tr>
  );
};

const ProjectDemandRow = ({ project, onEditPlannedHours }) => (
  <tr className="border-b hover:bg-slate-50">
    <td className="py-3 px-4">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          project.is_profitable ? 'bg-green-100' : 'bg-slate-100'
        }`}>
          <Building2 className={`w-4 h-4 ${
            project.is_profitable ? 'text-green-600' : 'text-slate-600'
          }`} />
        </div>
        <div>
          <p className="font-medium">{project.project_name}</p>
          {project.revenue_eur_monthly > 0 && (
            <p className="text-xs text-green-600">
              €{project.revenue_eur_monthly.toLocaleString()}/lună
            </p>
          )}
        </div>
      </div>
    </td>
    <td className="py-3 px-4 text-center">
      <div className="flex items-center gap-2 justify-center">
        {project.planned_hours !== null ? (
          <span className="font-medium">{project.planned_hours.toFixed(0)}h</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
        <button 
          onClick={() => onEditPlannedHours(project)}
          className="p-1 hover:bg-slate-100 rounded"
        >
          <Edit2 className="w-3 h-3 text-muted-foreground" />
        </button>
      </div>
    </td>
    <td className="py-3 px-4 text-center text-muted-foreground">
      {project.historical_avg_hours.toFixed(0)}h
    </td>
    <td className="py-3 px-4 text-center font-medium">
      {project.demand_hours.toFixed(0)}h
    </td>
    <td className="py-3 px-4 text-center">
      {project.actual_hours.toFixed(1)}h
    </td>
  </tr>
);

// Helper: get week start (Monday) and end (Friday)
const getWeekRange = (offset = 0) => {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday + offset * 7);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  return {
    start: monday.toISOString().split('T')[0],
    end: friday.toISOString().split('T')[0],
    label: offset === 0 ? 'Săptămâna curentă' : offset === 1 ? 'Săptămâna viitoare' : offset === -1 ? 'Săptămâna trecută' : 'Custom'
  };
};

const statusConfig = {
  underloaded: { label: 'Disponibil', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  busy: { label: 'Încărcat', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  overloaded: { label: 'Supraîncărcat', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  leave: { label: 'Concediu', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' }
};

// Employee Detail Modal with daily breakdown
const EmployeeAvailabilityModal = ({ isOpen, onClose, employee }) => {
  if (!employee) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {employee.user_name} - Situație Săptămânală
          </DialogTitle>
          <DialogDescription>
            Capacitate: {employee.daily_capacity}h/zi | Total: {employee.total_capacity}h | Disponibil: {employee.available_hours}h
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3">
          {employee.daily_breakdown?.map((day) => {
            const cfg = statusConfig[day.status] || statusConfig.underloaded;
            return (
              <div key={day.date} className={`rounded-lg border p-4 ${day.is_leave ? 'bg-purple-50 border-purple-200' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="text-center min-w-[60px]">
                      <p className="font-bold text-lg">{day.day_name}</p>
                      <p className="text-xs text-muted-foreground">{day.date.slice(5)}</p>
                    </div>
                    <Badge className={cfg.color}>{cfg.label}</Badge>
                  </div>
                  <div className="text-right">
                    {day.is_leave ? (
                      <div className="flex items-center gap-1 text-purple-600">
                        <Palmtree className="w-4 h-4" />
                        <span className="font-medium">Concediu</span>
                      </div>
                    ) : day.available > 0 ? (
                      <p className="text-green-600 font-bold text-lg">{day.available}h disponibil</p>
                    ) : day.available === 0 ? (
                      <p className="text-orange-600 font-medium">Plin</p>
                    ) : (
                      <p className="text-red-600 font-bold">{Math.abs(day.available)}h overloaded</p>
                    )}
                  </div>
                </div>
                
                {!day.is_leave && (
                  <div className="space-y-2">
                    {/* Capacity bar */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className="w-20 text-muted-foreground">Capacitate:</span>
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            day.available > 0 ? 'bg-green-400' : 'bg-red-400'
                          }`}
                          style={{ width: `${Math.min((day.used_hours / day.capacity) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="w-20 text-right font-medium">{day.used_hours}h / {day.capacity}h</span>
                    </div>
                    
                    {/* Tasks for this day */}
                    {day.tasks?.length > 0 ? (
                      <div className="mt-2 pl-2 border-l-2 border-slate-200 space-y-1">
                        {day.tasks.map((task) => (
                          <div key={task.id} className="flex items-center justify-between text-sm py-1">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${
                                task.status === 'done' ? 'bg-green-500' : 
                                task.status === 'in_progress' ? 'bg-blue-500' : 'bg-slate-400'
                              }`} />
                              <span className="truncate max-w-[300px]">{task.title}</span>
                              {task.scheduled_time && (
                                <span className="text-xs text-muted-foreground">{task.scheduled_time}</span>
                              )}
                            </div>
                            <span className="text-muted-foreground text-xs">{task.estimated_hours}h</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic pl-2">Niciun task programat</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Availability Section
const AvailabilitySection = ({ api }) => {
  const [periodType, setPeriodType] = useState('current');
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  
  useEffect(() => {
    loadAvailability();
  }, [periodType]);
  
  const getDateRange = () => {
    if (periodType === 'current') return getWeekRange(0);
    if (periodType === 'next') return getWeekRange(1);
    if (periodType === 'prev') return getWeekRange(-1);
    if (periodType === 'custom' && customStart && customEnd) return { start: customStart, end: customEnd };
    return getWeekRange(0);
  };
  
  const loadAvailability = async () => {
    const range = getDateRange();
    if (!range?.start || !range?.end) return;
    
    setLoading(true);
    try {
      const res = await api.get(`/capacity-planning/availability?start_date=${range.start}&end_date=${range.end}`);
      setAvailability(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea disponibilității');
    } finally {
      setLoading(false);
    }
  };
  
  const range = getDateRange();
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Disponibilitate Echipă
            </CardTitle>
            <CardDescription>
              Capacitate - Taskuri - Concedii = Ore disponibile
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={periodType} onValueChange={setPeriodType}>
              <SelectTrigger className="w-[200px]" data-testid="availability-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prev">Săptămâna trecută</SelectItem>
                <SelectItem value="current">Săptămâna curentă</SelectItem>
                <SelectItem value="next">Săptămâna viitoare</SelectItem>
                <SelectItem value="custom">Perioadă custom</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadAvailability} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {periodType === 'custom' && (
          <div className="flex items-center gap-2 mt-3">
            <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="w-[160px]" />
            <Minus className="w-4 h-4 text-muted-foreground" />
            <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="w-[160px]" />
            <Button size="sm" onClick={loadAvailability} disabled={!customStart || !customEnd}>Aplică</Button>
          </div>
        )}
        
        {availability && (
          <div className="flex items-center gap-4 mt-3 text-sm">
            <span className="text-muted-foreground">{range?.start} → {range?.end}</span>
            <Badge variant="outline">{availability.working_days} zile lucrătoare</Badge>
            <Badge variant="outline" className="bg-blue-50">Cap: {availability.total_capacity}h</Badge>
            <Badge variant="outline" className="bg-green-50 text-green-700">Disp: {availability.total_available}h</Badge>
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Se încarcă...</div>
        ) : !availability ? (
          <div className="text-center py-8 text-muted-foreground">Selectează o perioadă</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="availability-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left py-3 px-4 font-medium">Angajat</th>
                  <th className="text-center py-3 px-4 font-medium">Cap/Zi</th>
                  <th className="text-center py-3 px-4 font-medium">Capacitate</th>
                  <th className="text-center py-3 px-4 font-medium">Taskuri</th>
                  <th className="text-center py-3 px-4 font-medium">Concediu</th>
                  <th className="text-center py-3 px-4 font-medium font-bold text-blue-700">Ore Disponibile</th>
                  <th className="text-center py-3 px-4 font-medium">Status</th>
                  <th className="text-center py-3 px-4 font-medium">Detalii</th>
                </tr>
              </thead>
              <tbody>
                {availability.employees.map((emp) => {
                  const cfg = statusConfig[emp.status] || statusConfig.underloaded;
                  return (
                    <tr key={emp.user_id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedEmployee(emp)}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium">{emp.user_name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{emp.role.replace('_', ' ')}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center text-sm">{emp.daily_capacity}h</td>
                      <td className="py-3 px-4 text-center">{emp.total_capacity}h</td>
                      <td className="py-3 px-4 text-center">{emp.used_hours}h</td>
                      <td className="py-3 px-4 text-center">
                        {emp.leave_hours > 0 ? (
                          <span className="text-purple-600">{emp.leave_hours}h ({emp.leave_days_count}z)</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-lg font-bold ${
                          emp.available_hours > 0 ? 'text-green-600' : emp.available_hours === 0 ? 'text-orange-600' : 'text-red-600'
                        }`}>
                          {emp.available_hours}h
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={cfg.color}>{cfg.label}</Badge>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedEmployee(emp); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      
      <EmployeeAvailabilityModal
        isOpen={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        employee={selectedEmployee}
      />
    </Card>
  );
};

export const CapacityPlanning = () => {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // Edit dialogs
  const [editCapacityDialog, setEditCapacityDialog] = useState(null);
  const [editPlannedHoursDialog, setEditPlannedHoursDialog] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadReport();
  }, [month]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/capacity-planning/${month}`);
      setReport(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea raportului');
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (delta) => {
    const date = new Date(month + '-01');
    date.setMonth(date.getMonth() + delta);
    setMonth(date.toISOString().slice(0, 7));
  };

  const saveCapacity = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${editCapacityDialog.user_id}/daily-capacity`, {
        daily_capacity_hours: parseFloat(editValue)
      });
      toast.success('Capacitatea a fost actualizată!');
      setEditCapacityDialog(null);
      loadReport();
    } catch (err) {
      toast.error('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const savePlannedHours = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${editPlannedHoursDialog.project_id}/planned-hours`, {
        planned_hours_monthly: editValue ? parseFloat(editValue) : null
      });
      toast.success('Orele planificate au fost actualizate!');
      setEditPlannedHoursDialog(null);
      loadReport();
    } catch (err) {
      toast.error('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const formatMonth = (m) => {
    const date = new Date(m + '-01');
    return date.toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        </div>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <p>Nu s-a putut încărca raportul</p>
          <Button onClick={loadReport} className="mt-4">Reîncearcă</Button>
        </div>
      </Layout>
    );
  }

  const healthColors = {
    healthy: 'from-green-500 to-emerald-600',
    understaffed: 'from-orange-500 to-amber-600',
    overstaffed: 'from-yellow-500 to-amber-500',
    slightly_overstaffed: 'from-yellow-400 to-amber-500',
    critical: 'from-red-500 to-rose-600',
    data_error: 'from-yellow-400 to-amber-500'
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Target className="w-7 h-7 text-blue-500" />
              Capacity Planning
            </h1>
            <p className="text-muted-foreground">
              Planificarea capacității echipei și a cererii de proiecte
            </p>
          </div>
          
          {/* Month Selector */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => changeMonth(-1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="px-4 py-2 bg-slate-100 rounded-lg min-w-[180px] text-center font-medium">
              {formatMonth(month)}
            </div>
            <Button variant="outline" size="icon" onClick={() => changeMonth(1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={loadReport}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Health Status Banner */}
        <Card className={`bg-gradient-to-r ${healthColors[report.health_status]} text-white`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {report.health_status === 'healthy' ? (
                  <CheckCircle className="w-10 h-10" />
                ) : (
                  <AlertTriangle className="w-10 h-10" />
                )}
                <div>
                  <h2 className="text-xl font-bold">{report.health_message}</h2>
                  <p className="text-sm opacity-90">
                    {report.working_days_in_month} zile lucrătoare • {report.team_size} angajați
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">
                  {(report.planned_utilization * 100).toFixed(0)}%
                </p>
                <p className="text-sm opacity-90">Utilizare Planificată</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        {report.alerts.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Atenție</p>
                  <ul className="mt-1 space-y-1">
                    {report.alerts.map((alert, i) => (
                      <li key={i} className="text-sm text-amber-700">{alert}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <KPICard
            title="Capacitate Echipă"
            value={`${report.team_total_capacity.toFixed(0)}h`}
            subtitle={`${report.avg_capacity_per_person.toFixed(0)}h mediu/pers`}
            icon={Users}
            color="text-blue-600"
          />
          <KPICard
            title="Capacitate Clienți"
            value={`${report.team_client_capacity.toFixed(0)}h`}
            subtitle={`la ${(report.target_utilization * 100).toFixed(0)}% target`}
            icon={Target}
            color="text-purple-600"
          />
          <KPICard
            title="Demand Total"
            value={`${report.total_demand.toFixed(0)}h`}
            subtitle={`+${(report.buffer_percent * 100).toFixed(0)}% buffer`}
            icon={TrendingUp}
            color="text-green-600"
          />
          <KPICard
            title="Demand + Buffer"
            value={`${report.demand_with_buffer.toFixed(0)}h`}
            icon={Activity}
            color="text-orange-600"
          />
          <KPICard
            title="Gap Capacitate"
            value={`${report.capacity_gap_hours >= 0 ? '+' : ''}${report.capacity_gap_hours.toFixed(0)}h`}
            subtitle={report.capacity_gap_hours >= 0 ? 'Surplus' : 'Deficit'}
            icon={report.capacity_gap_hours >= 0 ? TrendingUp : TrendingDown}
            color={report.capacity_gap_hours >= 0 ? 'text-green-600' : 'text-red-600'}
          />
          <KPICard
            title="FTE Necesar"
            value={report.fte_needed.toFixed(1)}
            subtitle={`${report.fte_current} actual (${report.fte_delta >= 0 ? '+' : ''}${report.fte_delta.toFixed(1)})`}
            icon={Users}
            color={Math.abs(report.fte_delta) < 0.5 ? 'text-green-600' : 'text-amber-600'}
          />
        </div>

        {/* Employees Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Capacitate per Angajat
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium">Angajat</th>
                    <th className="text-center py-3 px-4 font-medium">Capacitate/Zi</th>
                    <th className="text-center py-3 px-4 font-medium">Total Lună</th>
                    <th className="text-center py-3 px-4 font-medium">Ore Lucrate</th>
                    <th className="text-center py-3 px-4 font-medium">Utilizare</th>
                    <th className="text-center py-3 px-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.employees.map((emp) => (
                    <EmployeeCapacityRow 
                      key={emp.user_id} 
                      employee={emp}
                      onEditCapacity={(e) => {
                        setEditCapacityDialog(e);
                        setEditValue(e.daily_capacity_hours.toString());
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* ===== DISPONIBILITATE ECHIPĂ (Săptămânal) ===== */}
        <AvailabilitySection api={api} />

        {/* Projects Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Demand per Proiect
            </CardTitle>
            <CardDescription>
              Ore planificate și istorice per proiect/client
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium">Proiect</th>
                    <th className="text-center py-3 px-4 font-medium">Ore Planificate</th>
                    <th className="text-center py-3 px-4 font-medium">Medie 3 luni</th>
                    <th className="text-center py-3 px-4 font-medium">Demand</th>
                    <th className="text-center py-3 px-4 font-medium">Ore Actuale</th>
                  </tr>
                </thead>
                <tbody>
                  {report.projects.map((proj) => (
                    <ProjectDemandRow 
                      key={proj.project_id} 
                      project={proj}
                      onEditPlannedHours={(p) => {
                        setEditPlannedHoursDialog(p);
                        setEditValue(p.planned_hours?.toString() || '');
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Edit Capacity Dialog */}
        <Dialog open={!!editCapacityDialog} onOpenChange={() => setEditCapacityDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Editează Capacitate Zilnică</DialogTitle>
              <DialogDescription>
                Setează orele zilnice pentru {editCapacityDialog?.user_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  min={1}
                  max={24}
                  step={0.5}
                  className="text-lg"
                />
                <span className="text-muted-foreground">ore/zi</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Standard: 8 ore/zi. Ajustează pentru part-time sau alte cazuri.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditCapacityDialog(null)}>
                Anulează
              </Button>
              <Button onClick={saveCapacity} disabled={saving}>
                {saving ? 'Se salvează...' : 'Salvează'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Planned Hours Dialog */}
        <Dialog open={!!editPlannedHoursDialog} onOpenChange={() => setEditPlannedHoursDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Editează Ore Planificate</DialogTitle>
              <DialogDescription>
                Setează orele planificate lunar pentru {editPlannedHoursDialog?.project_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  min={0}
                  step={1}
                  placeholder="Lasă gol pentru istoric"
                  className="text-lg"
                />
                <span className="text-muted-foreground">ore/lună</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Istoric 3 luni: {editPlannedHoursDialog?.historical_avg_hours?.toFixed(0) || 0}h mediu.
                Lasă gol pentru a folosi media istorică.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditPlannedHoursDialog(null)}>
                Anulează
              </Button>
              <Button onClick={savePlannedHours} disabled={saving}>
                {saving ? 'Se salvează...' : 'Salvează'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CapacityPlanning;
